import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";
import matrixosLogo from "../assets/images/matrixos-logo-transparent.png";

const INTEREST_OPTIONS = [
  { value: "ecosystem", label: "The full MatrixOS operational platform" },
  { value: "neo", label: "Neo — Workforce Operations" },
  { value: "morpheus", label: "Morpheus — Inventory Intelligence" },
  { value: "link", label: "Link — Movement & Execution" },
  { value: "league", label: "Matrix League — Performance Competition" },
  { value: "unsure", label: "Not sure yet — help us identify the right starting point" },
];

const navItems = [
  { label: "Platform", href: "/?home=1" },
  { label: "Neo", href: "/?home=1&layer=neo" },
  { label: "Morpheus", href: "/?home=1&layer=morpheus" },
  { label: "Link", href: "/?home=1&layer=link", soon: true },
  { label: "Matrix League", href: "/?home=1&layer=matrix-league" },
  { label: "Integrations", href: "/?home=1&section=integrations" },
];

function Toast({ open, title, message, tone = "success", onClose }) {
  if (!open) return null;
  return (
    <div className="mx-contact-toast-wrap" role="status" aria-live="polite">
      <div className={`mx-contact-toast ${tone === "success" ? "is-success" : "is-error"}`}>
        <div>
          <strong>{title}</strong>
          <p>{message}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close notification">×</button>
      </div>
    </div>
  );
}

function Field({ label, children, full = false }) {
  return (
    <label className={`mx-contact-field-wrap ${full ? "is-full" : ""}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

export default function Discovery() {
  const [searchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState({
    business: "",
    industry: "Retail / Hospitality",
    stores: "",
    staff: "",
    product: "unsure",
    systems: "",
    name: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, title: "", message: "", tone: "success" });

  const canSubmit = useMemo(
    () =>
      form.business.trim() &&
      form.name.trim() &&
      form.email.trim() &&
      form.phone.trim() &&
      form.stores &&
      form.consent,
    [form]
  );

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    const product = (searchParams.get("product") || "").trim();
    if (INTEREST_OPTIONS.some((option) => option.value === product)) set("product", product);
  }, [searchParams]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || submitting) return;

    try {
      setSubmitting(true);
      await addDoc(collection(db, "public_leads_founding_partner"), {
        createdAt: serverTimestamp(),
        source: "website",
        page: "discovery",
        leadType: "discovery_request",
        sendTo: "admin@matrixos.co.za",
        notificationEmail: "admin@matrixos.co.za",
        business: form.business.trim(),
        industry: form.industry,
        stores: Number(form.stores) || form.stores,
        staff: Number(form.staff) || form.staff,
        existingSystems: form.systems.trim(),
        productInterest: form.product,
        productInterestLabel: INTEREST_OPTIONS.find((option) => option.value === form.product)?.label || form.product,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        consentAccepted: true,
        consentAcceptedAt: serverTimestamp(),
        userAgent: navigator.userAgent,
      });

      setForm({
        business: "",
        industry: "Retail / Hospitality",
        stores: "",
        staff: "",
        product: "unsure",
        systems: "",
        name: "",
        email: "",
        phone: "",
        message: "",
        consent: false,
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
      setToast({
        open: true,
        title: "Discovery request received",
        message: "Thanks. We’ll review the operation you described and get back to you to arrange a discovery conversation.",
        tone: "success",
      });
    } catch (error) {
      console.error(error);
      setToast({
        open: true,
        title: "Could not send request",
        message: "Please try again. If the issue continues, email admin@matrixos.co.za.",
        tone: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="mx-contact-page">
      <Toast
        open={toast.open}
        title={toast.title}
        message={toast.message}
        tone={toast.tone}
        onClose={() => setToast({ open: false, title: "", message: "", tone: "success" })}
      />

      <style>{`
        :root { color-scheme: dark; }
        .mx-contact-page { min-height: 100vh; background: #020b15; color: #f8fbff; font-family: inherit; overflow-x: hidden; position: relative; }
        .mx-contact-page::before { content:""; position:absolute; inset:0; pointer-events:none; background: radial-gradient(circle at 20% 16%, rgba(0,184,255,.08), transparent 28%), radial-gradient(circle at 78% 38%, rgba(22,105,255,.07), transparent 34%); }
        .mx-contact-page * { box-sizing: border-box; }
        .mx-contact-header { position: sticky; top:0; z-index:50; height: 86px; border-bottom:1px solid rgba(94,183,255,.12); background:rgba(2,11,21,.92); backdrop-filter: blur(18px); }
        .mx-contact-header-inner { width:min(1500px, calc(100% - 72px)); height:100%; margin:0 auto; display:flex; align-items:center; gap:34px; }
        .mx-contact-brand { display:flex; align-items:center; margin-right:auto; }
        .mx-contact-logo { width: 172px; height:auto; display:block; }
        .mx-contact-nav { display:flex; align-items:center; gap:30px; }
        .mx-contact-nav a { color:#eef5fb; text-decoration:none; font-weight:750; font-size:14px; white-space:nowrap; transition:.2s ease; }
        .mx-contact-nav a:hover { color:#42c9ff; }
        .mx-contact-signin { display:inline-flex; align-items:center; justify-content:center; text-decoration:none; color:white; font-size:14px; font-weight:850; padding:11px 20px; border-radius:10px; border:1px solid rgba(84,195,255,.5); background:linear-gradient(180deg,#2dc9ff,#087ff0); box-shadow:0 10px 30px rgba(0,136,255,.2); white-space:nowrap; transition:.2s ease; }
        .mx-contact-signin:hover { transform:translateY(-1px); box-shadow:0 14px 34px rgba(0,149,255,.28); }
        .mx-contact-nav small { margin-left:6px; border:1px solid rgba(83,188,255,.3); color:#7ccdf4; border-radius:999px; padding:2px 6px; font-size:8px; text-transform:uppercase; letter-spacing:.08em; }
        .mx-contact-actions { display:flex; align-items:center; gap:24px; margin-left:auto; }
        .mx-contact-discovery { text-decoration:none; color:white; font-size:14px; font-weight:850; padding:11px 20px; border-radius:10px; border:1px solid rgba(84,195,255,.5); background:linear-gradient(180deg,#2dc9ff,#087ff0); box-shadow:0 10px 30px rgba(0,136,255,.2); }
        .mx-contact-menu { display:none; background:none; border:0; color:white; font-size:25px; cursor:pointer; }
        .mx-contact-menu-backdrop { display:none; }
        .mx-contact-mobile { display:none; }

        .mx-contact-shell { position:relative; z-index:1; width:min(1420px, calc(100% - 72px)); margin:0 auto; padding:76px 0 90px; display:grid; grid-template-columns:.9fr 1.1fr; gap:64px; align-items:start; }
        .mx-contact-copy { padding:35px 0 0; position:sticky; top:125px; }
        .mx-contact-eyebrow { display:block; color:#23c7ff; font-size:12px; font-weight:900; letter-spacing:.22em; text-transform:uppercase; margin-bottom:25px; }
        .mx-contact-copy h1 { margin:0; max-width:690px; font-size:clamp(48px,4.9vw,78px); line-height:.99; letter-spacing:-.055em; font-weight:900; }
        .mx-contact-copy h1 strong { color:#129eff; font-weight:inherit; }
        .mx-contact-copy > p { margin:28px 0 0; max-width:650px; font-size:19px; line-height:1.7; color:#a9bac9; }
        .mx-contact-principles { margin-top:42px; display:grid; gap:14px; max-width:660px; }
        .mx-contact-principle { padding:19px 20px; border:1px solid rgba(70,177,238,.17); border-radius:15px; background:rgba(7,25,42,.58); display:grid; grid-template-columns:42px 1fr; gap:15px; align-items:start; }
        .mx-contact-principle b { color:#f8fbff; display:block; font-size:15px; margin-bottom:5px; }
        .mx-contact-principle p { color:#859cad; font-size:13px; line-height:1.55; margin:0; }
        .mx-contact-principle i { font-style:normal; width:38px; height:38px; border-radius:11px; display:grid; place-items:center; color:#32c8ff; background:rgba(19,143,218,.1); border:1px solid rgba(50,200,255,.18); font-weight:900; }

        .mx-contact-form-card { border:1px solid rgba(82,178,234,.22); border-radius:24px; padding:38px; background:linear-gradient(145deg,rgba(8,30,50,.94),rgba(4,18,32,.96)); box-shadow:0 28px 90px rgba(0,0,0,.28); }
        .mx-contact-form-card .intro { padding-bottom:28px; border-bottom:1px solid rgba(80,180,240,.13); margin-bottom:28px; }
        .mx-contact-form-card .intro span { color:#23c7ff; font-size:11px; letter-spacing:.22em; font-weight:900; text-transform:uppercase; }
        .mx-contact-form-card h2 { margin:10px 0 12px; font-size:clamp(30px,3vw,43px); letter-spacing:-.04em; line-height:1.05; }
        .mx-contact-form-card .intro p { margin:0; color:#98acbc; line-height:1.65; font-size:15px; }
        .mx-contact-grid { display:grid; grid-template-columns:1fr 1fr; gap:22px 18px; }
        .mx-contact-field-wrap { min-width:0; }
        .mx-contact-field-wrap.is-full { grid-column:1/-1; }
        .mx-contact-field-wrap > span { display:block; margin-bottom:8px; color:#75d7ff; font-size:10px; letter-spacing:.21em; text-transform:uppercase; font-weight:900; }
        .mx-contact-input { width:100%; border:1px solid rgba(141,198,231,.42); border-radius:13px; background:#061424; color:#fff; padding:15px 16px; font:inherit; font-size:15px; outline:none; transition:.2s ease; }
        .mx-contact-input:hover { border-color:rgba(86,200,255,.55); }
        .mx-contact-input:focus { border-color:#38c7ff; box-shadow:0 0 0 3px rgba(56,199,255,.08),0 0 28px rgba(0,149,255,.08); }
        .mx-contact-input::placeholder { color:#667d90; }
        select.mx-contact-input { color-scheme:dark; }
        textarea.mx-contact-input { min-height:132px; resize:vertical; line-height:1.55; }
        .mx-contact-consent { margin-top:22px; padding:15px 17px; border:1px solid rgba(77,163,216,.17); border-radius:13px; background:rgba(5,20,35,.5); display:flex; align-items:flex-start; gap:12px; color:#9fb2c1; font-size:13px; line-height:1.5; }
        .mx-contact-consent input { margin-top:3px; width:17px; height:17px; accent-color:#20bfff; }
        .mx-contact-submit { margin-top:22px; width:100%; border:1px solid rgba(76,203,255,.44); border-radius:13px; padding:16px 20px; background:linear-gradient(180deg,#2cc7ff,#087ff0); color:#fff; font:inherit; font-weight:900; letter-spacing:.04em; cursor:pointer; box-shadow:0 14px 34px rgba(0,132,255,.18); transition:.22s ease; }
        .mx-contact-submit:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 18px 40px rgba(0,149,255,.26); }
        .mx-contact-submit:disabled { opacity:.4; cursor:not-allowed; }
        .mx-contact-note { text-align:center; margin:13px 0 0; color:#5f788c; font-size:11px; }
        .mx-contact-toast-wrap { position:fixed; right:24px; top:105px; z-index:100; }
        .mx-contact-toast { width:min(390px,calc(100vw - 48px)); border-radius:17px; padding:18px; display:flex; justify-content:space-between; gap:18px; border:1px solid rgba(82,203,255,.36); background:#071b2e; box-shadow:0 20px 60px rgba(0,0,0,.4); }
        .mx-contact-toast.is-error { border-color:rgba(251,113,133,.45); }
        .mx-contact-toast strong { font-size:13px; text-transform:uppercase; letter-spacing:.12em; }
        .mx-contact-toast p { margin:7px 0 0; color:#aac0cf; font-size:13px; line-height:1.5; }
        .mx-contact-toast button { border:0; background:none; color:#d7e5ee; font-size:21px; cursor:pointer; }

        .mx-home-footer { position:relative; z-index:1; background:#020b15; border-top:1px solid rgba(62,151,225,.16); padding:28px clamp(20px,4vw,64px) 30px; color:#8fa6ba; }
        .mx-home-footer-inner { width:min(1480px,100%); margin:0 auto; display:grid; grid-template-columns:minmax(260px,1fr) auto minmax(260px,1fr); align-items:center; gap:28px; }
        .mx-home-footer-brand { display:flex; align-items:center; gap:14px; min-width:0; }
        .mx-home-footer-brand div { display:grid; gap:2px; }
        .mx-home-footer-brand strong { color:#f6fbff; font-size:.96rem; letter-spacing:.01em; }
        .mx-home-footer-brand span { color:#6f8aa2; font-size:.78rem; }
        .mx-home-footer-brand small { color:#536f86; font-size:.72rem; line-height:1.45; font-weight:600; }
        .mx-home-footer-links { display:flex; align-items:center; justify-content:center; gap:26px; }
        .mx-home-footer-links a { color:#9fb3c5; text-decoration:none; font-size:.88rem; font-weight:700; transition:color .2s ease; }
        .mx-home-footer-links a:hover { color:#34b9ff; }
        .mx-home-footer-legal { display:flex; justify-content:flex-end; text-align:right; font-size:.8rem; color:#647d93; }

        @media (max-width: 1100px) {
          .mx-contact-nav { display:none; }
          .mx-contact-menu { display:block; position:relative; z-index:32; }
          .mx-contact-menu-backdrop { display:block; position:fixed; inset:0; z-index:29; border:0; background:rgba(0,7,16,.34); padding:0; margin:0; }
          .mx-contact-header-inner { position:relative; z-index:31; }
          .mx-contact-header-inner { width:min(100% - 40px, 1420px); }
          .mx-contact-mobile.is-open { display:block; position:absolute; z-index:33; left:20px; right:20px; top:76px; padding:14px; border:1px solid rgba(71,178,238,.34); border-radius:15px; background:#061a2b; box-shadow:0 24px 70px rgba(0,0,0,.52), 0 0 0 1px rgba(57,201,255,.05); }
          .mx-contact-mobile a { display:flex; padding:13px 14px; color:#eef7fd; text-decoration:none; font-weight:750; border-radius:9px; }
          .mx-contact-mobile a:hover { background:rgba(42,183,255,.08); color:#39c9ff; }
          .mx-contact-shell { grid-template-columns:1fr; width:min(100% - 46px, 880px); gap:40px; padding-top:48px; }
          .mx-contact-copy { position:static; padding-top:10px; }
          .mx-contact-principles { grid-template-columns:repeat(3,1fr); max-width:none; }
          .mx-contact-principle { grid-template-columns:1fr; }

          .mx-home-footer-inner { grid-template-columns:1fr auto; }
          .mx-home-footer-links { justify-content:flex-end; }
          .mx-home-footer-legal { grid-column:1 / -1; justify-content:flex-start; text-align:left; padding-top:14px; border-top:1px solid rgba(62,151,225,.11); }
        }
        @media (max-width: 700px) {
          .mx-contact-header { height:72px; }
          .mx-contact-header-inner { width:calc(100% - 28px); gap:14px; }
          .mx-contact-logo { width:145px; }
          .mx-contact-signin { display:inline-flex; padding:11px 14px; font-size:12px; }
          .mx-contact-discovery { padding:11px 13px; font-size:12px; }
          .mx-contact-mobile.is-open { top:65px; left:12px; right:12px; }
          .mx-contact-shell { width:calc(100% - 28px); padding:39px 0 65px; }
          .mx-contact-copy h1 { font-size:44px; }
          .mx-contact-copy > p { font-size:16px; }
          .mx-contact-principles { grid-template-columns:1fr; }
          .mx-contact-form-card { padding:24px 18px; border-radius:18px; }
          .mx-contact-grid { grid-template-columns:1fr; }
          .mx-contact-field-wrap.is-full { grid-column:auto; }

          .mx-home-footer { padding:26px 18px 28px; }
          .mx-home-footer-inner { grid-template-columns:1fr; gap:20px; }
          .mx-home-footer-links { justify-content:flex-start; gap:22px; flex-wrap:wrap; }
          .mx-home-footer-legal { grid-column:1; padding-top:18px; }
        }
      `}</style>

      <header className="mx-contact-header">
        <div className="mx-contact-header-inner">
          <a className="mx-contact-brand" href="/" aria-label="MatrixOS home">
            <img className="mx-contact-logo" src={matrixosLogo} alt="MatrixOS" />
          </a>

          <nav className="mx-contact-nav" aria-label="Primary navigation">
            {navItems.map((item) => (
              <a key={item.label} href={item.href}>
                {item.label}{item.soon && <small>Soon</small>}
              </a>
            ))}
          </nav>

          <div className="mx-contact-actions">
            <a className="mx-contact-signin" href="/login">Sign in</a>
            <button type="button" className="mx-contact-menu" onClick={() => setMenuOpen((open) => !open)} aria-label="Toggle navigation">☰</button>
          </div>

          {menuOpen && (
            <button
              type="button"
              className="mx-contact-menu-backdrop"
              aria-label="Close navigation menu"
              onClick={() => setMenuOpen(false)}
            />
          )}

          <div className={`mx-contact-mobile ${menuOpen ? "is-open" : ""}`}>
            {navItems.map((item) => <a key={item.label} href={item.href} onClick={() => setMenuOpen(false)}>{item.label}{item.soon ? " · Coming Soon" : ""}</a>)}
            <a href="/login">Sign in</a>
          </div>
        </div>
      </header>

      <section className="mx-contact-shell" id="discovery">
        <div className="mx-contact-copy">
          <span className="mx-contact-eyebrow">MATRIXOS DISCOVERY</span>
          <h1>Show us how your <strong>operation actually runs.</strong></h1>
          <p>
            Discovery starts with your operation, not our software. Tell us what systems you already use,
            where visibility breaks down, and what your teams spend too much time chasing. We’ll use that
            context to identify where MatrixOS could sit above your existing stack and add the most value.
          </p>

          <div className="mx-contact-principles">
            <div className="mx-contact-principle"><i>01</i><div><b>Keep what already works.</b><p>POS, payroll, inventory and other systems can remain your systems of record.</p></div></div>
            <div className="mx-contact-principle"><i>02</i><div><b>Find the operational gap.</b><p>We want to understand where visibility, accountability or response currently breaks down.</p></div></div>
            <div className="mx-contact-principle"><i>03</i><div><b>Start where it matters.</b><p>You do not need every MatrixOS layer on day one. Discovery helps identify the strongest starting point.</p></div></div>
          </div>
        </div>

        <form className="mx-contact-form-card" onSubmit={handleSubmit}>
          <div className="intro">
            <span>BOOK A DISCOVERY</span>
            <h2>Tell us about your operation.</h2>
            <p>No scripted demo first. Give us enough context to understand the operation, and we’ll follow up for a proper discovery conversation.</p>
          </div>

          <div className="mx-contact-grid">
            <Field label="Business name" full>
              <input className="mx-contact-input" value={form.business} onChange={(e) => set("business", e.target.value)} placeholder="Example: Matrix Foods" />
            </Field>

            <Field label="Industry">
              <select className="mx-contact-input" value={form.industry} onChange={(e) => set("industry", e.target.value)}>
                <option>Retail / Hospitality</option>
                <option>Quick Service Restaurant</option>
                <option>Restaurant Group</option>
                <option>Retail</option>
                <option>Warehousing / Distribution</option>
                <option>Other Multi-Site Operation</option>
              </select>
            </Field>

            <Field label="Where should we start?">
              <select className="mx-contact-input" value={form.product} onChange={(e) => set("product", e.target.value)}>
                {INTEREST_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </Field>

            <Field label="Sites / stores">
              <input className="mx-contact-input" type="number" min="1" value={form.stores} onChange={(e) => set("stores", e.target.value)} placeholder="Example: 8" />
            </Field>

            <Field label="Approx. staff / users">
              <input className="mx-contact-input" type="number" min="0" value={form.staff} onChange={(e) => set("staff", e.target.value)} placeholder="Example: 120" />
            </Field>

            <Field label="Systems you already use" full>
              <input className="mx-contact-input" value={form.systems} onChange={(e) => set("systems", e.target.value)} placeholder="Example: GAAP, Pilot, Oracle MICROS, Lightspeed, payroll, delivery..." />
            </Field>

            <Field label="Your name">
              <input className="mx-contact-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" />
            </Field>

            <Field label="Phone / WhatsApp">
              <input className="mx-contact-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+27 ..." />
            </Field>

            <Field label="Email" full>
              <input className="mx-contact-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.co.za" />
            </Field>

            <Field label="What should MatrixOS help you understand or improve?" full>
              <textarea className="mx-contact-input" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Example: morning stock counts are being skipped, managers chase attendance manually, we lack visibility across sites, duties are not consistently completed..." />
            </Field>
          </div>

          <label className="mx-contact-consent">
            <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} />
            <span>I consent to MatrixOS storing these details so the team can contact me about this discovery request.</span>
          </label>

          <button className="mx-contact-submit" type="submit" disabled={!canSubmit || submitting}>
            {submitting ? "Sending…" : "Request a Discovery →"}
          </button>
          <p className="mx-contact-note">Your request is routed directly to MatrixOS for follow-up.</p>
        </form>
      </section>

      <footer className="mx-home-footer" aria-label="MatrixOS footer">
        <div className="mx-home-footer-inner">
          <div className="mx-home-footer-brand">
            <div>
              <strong>MatrixOS</strong>
              <span>Operate with Clarity.</span>
              <small>Built in South Africa for multi-site operations.</small>
              <small>B-BBEE Level 1 Contributor · 135% B-BBEE procurement recognition.</small>
            </div>
          </div>

          <nav className="mx-home-footer-links" aria-label="Footer navigation">
            <a href="/about">About</a>
            <a href="/contact">Contact</a>
            <a href="/terms">Terms</a>
            <a href="/privacy">Privacy</a>
            <a href="https://www.linkedin.com/company/112805381/" target="_blank" rel="noreferrer">LinkedIn ↗</a>
          </nav>

          <div className="mx-home-footer-legal">
            <span>© 2026 MatrixOS. All rights reserved.</span>
          </div>
        </div>
      </footer>

    </main>
  );
}
